import { useState, useEffect, useRef } from "react";
import { Bell, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_META = {
  games: { label: "Games", icon: "⚽" },
  academy: { label: "Academy", icon: "🎓" },
  membership: { label: "Membership", icon: "🪪" },
  approvals: { label: "Approvals", icon: "✅" },
  announcements: { label: "Announcements", icon: "📢" },
  other: { label: "Other", icon: "📬" },
};
const CATEGORY_ORDER = ["games", "academy", "membership", "approvals", "announcements", "other"];

export default function NotificationBell({ user }) {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const ref = useRef(null);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const r = await axios.get(`${API}/notifications/my-notifications`, { withCredentials: true });
      setNotifications(r.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications();
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/mark-read/${id}`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true, read: true } : n)));
      fetchUnreadCount();
    } catch (_) {}
  };

  const onItemClick = (n) => {
    if (!n.read && !n.is_read) markRead(n.id);
    setOpen(false);
    navigate(n.url || "/notifications");
  };

  const catOf = (n) => n.category || "other";
  const catCount = (key) =>
    notifications.filter((n) => (key === "all" ? true : catOf(n) === key)).length;

  const presentCats = CATEGORY_ORDER.filter((c) => catCount(c) > 0);
  const visible = notifications.filter((n) => (activeCat === "all" ? true : catOf(n) === activeCat));

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        className="relative p-2 hover:bg-slate-800 rounded-full transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell-button"
      >
        <Bell className="w-6 h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-red-500/100 rounded-full border-2 border-white" data-testid="notification-bell-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-slate-900 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="notification-bell-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="font-semibold text-white">Notifications</span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Category chips */}
          <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-white/5">
            <button
              onClick={() => setActiveCat("all")}
              data-testid="bell-cat-all"
              className={`text-xs px-2 py-1 rounded-full ${activeCat === "all" ? "bg-cyan-500 text-black font-semibold" : "bg-slate-800 text-slate-300"}`}
            >
              All ({catCount("all")})
            </button>
            {presentCats.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`bell-cat-${c}`}
                className={`text-xs px-2 py-1 rounded-full ${activeCat === c ? "bg-cyan-500 text-black font-semibold" : "bg-slate-800 text-slate-300"}`}
              >
                {CATEGORY_META[c].icon} {CATEGORY_META[c].label} ({catCount(c)})
              </button>
            ))}
          </div>

          {/* List */}
          <div className="max-h-[340px] overflow-y-auto divide-y divide-white/5" data-testid="bell-notification-list">
            {visible.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-400 text-sm">You're all caught up!</div>
            ) : (
              visible.slice(0, 12).map((n) => {
                const isRead = n.read || n.is_read;
                return (
                  <button
                    key={n.id}
                    onClick={() => onItemClick(n)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-800/60 transition-colors ${isRead ? "" : "bg-cyan-500/5"}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg leading-none mt-0.5">{CATEGORY_META[catOf(n)]?.icon || "📬"}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${isRead ? "text-slate-300" : "text-white font-semibold"}`}>{n.title}</p>
                          {!isRead && <span className="flex-shrink-0 w-2 h-2 bg-cyan-400 rounded-full" />}
                        </div>
                        <p className="text-xs text-slate-400 line-clamp-2">{n.message}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-white/10">
            <button
              onClick={() => { setOpen(false); navigate("/notifications"); }}
              className="w-full text-center text-sm text-cyan-400 hover:text-cyan-300 font-medium"
              data-testid="bell-view-all"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
